import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import BottomNavigation from './components/BottomNavigation';
import MobileHeader from './components/MobileHeader';
import AdminBottomNavigation from './components/AdminBottomNavigation';
import AdminMobileHeader from './components/AdminMobileHeader';
import { useMessageNotifications } from './hooks/useMessageNotifications';
import { useNotifications } from './hooks/useNotifications';
import pushNotificationService from './services/pushNotifications';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import Profile from './pages/Profile';
import TripDetails from './pages/TripDetails';
import CreateTrip from './pages/CreateTrip';
import MyTrips from './pages/MyTrips';
import MyBookings from './pages/MyBookings';
import TripTracking from './pages/TripTracking';
import SearchTrips from './pages/SearchTrips';
import About from './pages/About';
import Help from './pages/Help';
import Safety from './pages/Safety';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Contact from './pages/Contact';
import RefundPolicy from './pages/RefundPolicy';
import CancellationPolicy from './pages/CancellationPolicy';
import ShippingPolicy from './pages/ShippingPolicy';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminTrips from './pages/admin/Trips';
import AdminBookings from './pages/admin/Bookings';
import AdminAnalytics from './pages/admin/Analytics';
import AdminPayouts from './pages/admin/Payouts';
import RiderVerification from './pages/RiderVerification';
import AdminLogin from './pages/admin/AdminLogin';
import Notifications from './pages/Notifications';
import More from './pages/More';
import Earnings from './pages/Earnings';

// Component to initialize notifications
const NotificationInitializer = () => {
  useMessageNotifications();
  useNotifications(); // Add booking notifications
  
  // Initialize push notifications for native platforms
  useEffect(() => {
    pushNotificationService.initialize();
    
    // Cleanup on unmount
    return () => {
      pushNotificationService.removeAllListeners();
    };
  }, []);
  
  return null;
};

function App() {
  return (
    <AuthProvider>
      <NotificationInitializer />
      <div className="min-h-screen bg-gray-50">
        {/* Desktop Navbar - hidden on mobile */}
        <div className="hidden md:block">
          <Navbar />
        </div>
        
        {/* Mobile Header - shown only on mobile (regular users) */}
        <MobileHeader />
        
        {/* Admin Mobile Header - shown only on mobile (admin users) */}
        <AdminMobileHeader />
        
        <main className="container mx-auto px-3 sm:px-4 py-4 sm:py-8 pb-20 md:pb-8">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/trip/:id" element={<TripDetails />} />
            <Route path="/search" element={<SearchTrips />} />
            <Route path="/about" element={<About />} />
            <Route path="/help" element={<Help />} />
            <Route path="/safety" element={<Safety />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            
            {/* Protected Routes */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            } />
            <Route path="/verify-rider" element={
              <ProtectedRoute allowedRoles={['rider']}>
                <RiderVerification />
              </ProtectedRoute>
            } />
            <Route path="/create-trip" element={
              <ProtectedRoute allowedRoles={['rider']}>
                <CreateTrip />
              </ProtectedRoute>
            } />
            <Route path="/my-trips" element={
              <ProtectedRoute allowedRoles={['rider']}>
                <MyTrips />
              </ProtectedRoute>
            } />
            <Route path="/my-bookings" element={
              <ProtectedRoute allowedRoles={['passenger']}>
                <MyBookings />
              </ProtectedRoute>
            } />
            <Route path="/track/:id" element={
              <ProtectedRoute>
                <TripTracking />
              </ProtectedRoute>
            } />
            <Route path="/notifications" element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            } />
            <Route path="/more" element={
              <ProtectedRoute>
                <More />
              </ProtectedRoute>
            } />
            <Route path="/earnings" element={
              <ProtectedRoute allowedRoles={['rider']}>
                <Earnings />
              </ProtectedRoute>
            } />
            
            {/* Admin Routes */}
            <Route path="/admin" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="/admin/users" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminUsers />
              </ProtectedRoute>
            } />
            <Route path="/admin/trips" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminTrips />
              </ProtectedRoute>
            } />
            <Route path="/admin/bookings" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminBookings />
              </ProtectedRoute>
            } />
            <Route path="/admin/analytics" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminAnalytics />
              </ProtectedRoute>
            } />
            <Route path="/admin/payouts" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <AdminPayouts />
              </ProtectedRoute>
            } />
          </Routes>
        </main>
        
        {/* Desktop Footer - hidden on mobile */}
        <div className="hidden md:block">
          <Footer />
        </div>
        
        {/* Bottom Navigation - shown only on mobile (regular users) */}
        <BottomNavigation />
        
        {/* Admin Bottom Navigation - shown only on mobile (admin users) */}
        <AdminBottomNavigation />
      </div>
    </AuthProvider>
  );
}

export default App;
